// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Client-side i18n bootstrap utilities.
 *
 * Constructs the Translator and manages language preference selection
 * and persistence for the browser SPA.
 */
import type { InitialState } from "../../shared/src/types";
import { addDynamicTranslationKeys } from "../../shared/src/dynamic-keys";
import { Translator } from "@unilarva/translator";
import {
  bindLanguageDetails,
  bindTranslator,
  type TranslatorDomBinding,
  type TranslatorLanguageDetailsBinding,
} from "@unilarva/translator/dom";
import { createRichTranslationExtension } from "./rich-translation-extension";
import { parseViewUrl, serializeViewUrl } from "../../shared/src/view-url";

/**
 * Manages client-side translator configuration and language preference handling.
 */
export class I18nManager {
  /** Shared translator instance for the SPA. */
  public readonly translator: Translator;

  /** Scoped text-only DOM binding for translator markers. */
  public readonly binding: TranslatorDomBinding;

  /** Package-owned synchronization for the application language dropdown. */
  private languageDetailsBinding: TranslatorLanguageDetailsBinding | null = null;

  private readonly state: InitialState;
  private readonly shouldChooseBrowserDefaultOnFirstActivate: boolean;
  private readonly initialUrlLanguage: string | null;
  private langFromUrlAllowedOnce = true;

  constructor(state: InitialState) {
    this.state = state;

    const initialUrl = new URL(window.location.href);
    const initialUrlLanguageValues = initialUrl.searchParams.getAll("lang");
    this.initialUrlLanguage =
      initialUrlLanguageValues.length === 1 ? initialUrlLanguageValues[0].trim() || null : null;
    const persistedLanguage = this.applyPersistedLanguagePreference();
    this.shouldChooseBrowserDefaultOnFirstActivate =
      persistedLanguage === null && this.getSupportedInitialUrlLanguage() === null;

    this.translator = this.createClientTranslator();
    this.binding = bindTranslator(this.translator, document.documentElement, {
      updateDocumentLanguage: true,
      extensions: [createRichTranslationExtension(this.state.site)],
    });
  }

  /**
   * Registers language-change handling and explicit UI preference persistence.
   */
  init(onLanguageChanged: (lang: string) => void): void {
    const languageDetails = document.querySelector<HTMLDetailsElement>(
      "[data-sb-language-details]",
    );
    if (languageDetails) {
      this.languageDetailsBinding = bindLanguageDetails(this.translator, languageDetails, {
        choiceClass: "sb-btn sb-btn--list sb-dropdown-item",
      });
      languageDetails.addEventListener("click", this.persistExplicitLanguageChoice);
    }
    this.translator.subscribe(({ language }) => {
      this.state.lang = language;
      this.refreshNavigationLanguages(language);
      onLanguageChanged(language);
    });
  }

  /** Keeps genuine navigation links shareable in the currently selected language. */
  private refreshNavigationLanguages(language: string): void {
    document.querySelectorAll<HTMLAnchorElement>("a[data-view-nav]").forEach(link => {
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== "/") return;
      link.setAttribute(
        "href",
        serializeViewUrl(
          { ...parseViewUrl(url), lang: language },
          {
            defaultLanguage: this.state.site.defaultLanguage ?? "en",
          },
        ),
      );
    });
  }

  /**
   * Applies the initial language based on ?lang=, persisted preference, or browser default.
   * Consumes the URL language once, then removes it without changing the saved preference.
   * Should be called once during bootstrap, after listeners are registered.
   */
  applyInitialLanguageFromUrlOrBrowser(): void {
    if (!this.langFromUrlAllowedOnce) return;
    const qLang = this.getSupportedInitialUrlLanguage();
    this.langFromUrlAllowedOnce = false;

    if (qLang) {
      this.translator.setLanguage(qLang, { force: true });
    } else if (this.shouldChooseBrowserDefaultOnFirstActivate) {
      const browserLanguage = this.getSupportedBrowserLanguage();
      this.translator.setLanguage(browserLanguage, { force: true });
    } else {
      this.translator.setLanguage(this.state.lang, { force: true });
    }

    this.stripLangParamFromUrl();
  }

  /** Persists a language only when its UI control is explicitly activated. */
  private persistExplicitLanguageChoice = (event: MouseEvent): void => {
    const rawTarget = event.target as unknown;
    const target =
      rawTarget instanceof Element
        ? rawTarget
        : ((rawTarget as { parentElement?: Element | null } | null)?.parentElement ?? null);
    const language = target
      ?.closest<HTMLElement>("[data-i18n-language]")
      ?.getAttribute("data-i18n-language")
      ?.trim();
    if (!language) return;

    localStorage.setItem("sb_lang", language);
    document.cookie = `sb_lang=${encodeURIComponent(language)}; Path=/; Max-Age=31536000; SameSite=Lax`;
    this.stripLangParamFromUrl();
  };

  /** Resolves browser preferences to one language supported by this site. */
  private getSupportedBrowserLanguage(): string {
    for (const preferred of navigator.languages?.length
      ? navigator.languages
      : [navigator.language]) {
      const normalized = preferred.replaceAll("_", "-");
      const exact = this.state.site.languages.find(
        language => language.toLowerCase() === normalized.toLowerCase(),
      );
      if (exact) return exact;
      const base = normalized.split("-")[0].toLowerCase();
      const baseMatch = this.state.site.languages.find(language => language.toLowerCase() === base);
      if (baseMatch) return baseMatch;
    }
    return this.state.site.defaultLanguage ?? this.state.lang ?? "en";
  }

  /**
   * Creates and configures the Translator instance from the initial state.
   */
  private createClientTranslator(): Translator {
    const t = new Translator({
      language: this.state.lang,
      fallbackLanguage: this.state.site.defaultLanguage ?? this.state.lang ?? "en",
    });

    const currentYear = String(new Date().getFullYear());
    const replaceMap: Record<string, string> = {
      ...(this.state.site.translationReplaces ?? {}),
      "%CURRENT_YEAR%": currentYear,
    };

    // Client-side only: construct contact email (avoid exposing it in SSR HTML).
    const ceh = (this.state.site.ceh ?? "").trim();
    const cet = (this.state.site.cet ?? "").trim();
    if (ceh && cet) {
      replaceMap["%CONTACT_EMAIL%"] = `${ceh}@${cet}`;
    }

    t.importTranslations(this.state.baseTranslations, { replacements: replaceMap });
    t.importTranslations(this.state.siteTranslations, { replacements: replaceMap });
    const registeredCodes = new Set(t.getLanguages().map(language => language.code));
    for (const code of this.state.site.languages) {
      if (!registeredCodes.has(code)) t.addLanguage({ code });
    }
    const selectableLanguages = new Set([...this.state.site.languages, "pe"]);
    for (const language of t.getLanguages()) {
      if (!selectableLanguages.has(language.code)) t.removeLanguage(language.code);
    }
    addDynamicTranslationKeys(t, this.state);
    t.setFallbackLanguage(this.state.site.defaultLanguage ?? this.state.lang ?? "en");
    t.setLanguage(this.state.lang, { force: true });
    return t;
  }

  /**
   * Restores the effective preference from LocalStorage first, then the cookie.
   */
  private applyPersistedLanguagePreference(): string | null {
    const storedLang = localStorage.getItem("sb_lang");

    if (storedLang && this.state.site.languages.includes(storedLang)) {
      this.state.lang = storedLang;
      return storedLang;
    }

    const cookieLang = this.readCookieLang();
    if (cookieLang && this.state.site.languages.includes(cookieLang)) {
      // Offline HTML may have been rendered before this preference was selected.
      this.state.lang = cookieLang;
      return cookieLang;
    }

    return null;
  }

  /**
   * Reads the persisted language from the sb_lang cookie, if present.
   */
  private readCookieLang(): string | null {
    const cookie = typeof document === "undefined" ? "" : document.cookie || "";
    if (!cookie) return null;
    const parts = cookie.split(";");
    for (const part of parts) {
      const [k, ...rest] = part.trim().split("=");
      if (k === "sb_lang") {
        const v = decodeURIComponent(rest.join("=") || "");
        return v || null;
      }
    }
    return null;
  }

  /** Returns the single supported language supplied by the initial URL. */
  private getSupportedInitialUrlLanguage(): string | null {
    const language = this.initialUrlLanguage;
    return language && this.state.site.languages.includes(language) ? language : null;
  }

  /**
   * Removes the ?lang= query parameter from the current URL without adding history.
   */
  private stripLangParamFromUrl(): void {
    const u = new URL(window.location.href);
    if (!u.searchParams.has("lang")) return;
    u.searchParams.delete("lang");
    window.history.replaceState(window.history.state, "", u);
  }
}
